import { useEffect } from 'react';
import { create } from 'zustand';
import { AlertTriangle, CheckCircle2, X, XCircle } from 'lucide-react';
import clsx from 'clsx';

/**
 * Minimal toast system for admin actions.
 *
 * Admin changes — activating a user, deleting a rule — succeed silently
 * otherwise, and an admin who is not sure whether their click registered will
 * click again. On destructive operations that is a real problem.
 */
type ToastKind = 'success' | 'error' | 'warning';

interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
}

interface ToastState {
  toasts: Toast[];
  push: (kind: ToastKind, message: string) => void;
  dismiss: (id: string) => void;
}

export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  push: (kind, message) =>
    set((state) => ({
      toasts: [...state.toasts, { id: crypto.randomUUID(), kind, message }],
    })),
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

/** Convenience wrappers so callers do not repeat the kind string. */
export const toast = {
  success: (message: string) => useToasts.getState().push('success', message),
  error: (message: string) => useToasts.getState().push('error', message),
  warning: (message: string) => useToasts.getState().push('warning', message),
};

const STYLES: Record<ToastKind, { className: string; Icon: typeof CheckCircle2 }> = {
  success: { className: 'border-ok/30 bg-ok/10 text-ok', Icon: CheckCircle2 },
  error: { className: 'border-danger/30 bg-danger/10 text-danger', Icon: XCircle },
  warning: { className: 'border-warn/30 bg-warn/10 text-warn', Icon: AlertTriangle },
};

export function ToastViewport() {
  const toasts = useToasts((s) => s.toasts);

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
      // Announced to screen readers without stealing focus.
      role="status"
      aria-live="polite"
    >
      {toasts.map((item) => (
        <ToastItem key={item.id} toast={item} />
      ))}
    </div>
  );
}

function ToastItem({ toast: item }: { toast: Toast }) {
  const dismiss = useToasts((s) => s.dismiss);
  const { className, Icon } = STYLES[item.kind];

  useEffect(() => {
    // Errors stay until dismissed. An error that vanishes before it is read is
    // worse than no error at all.
    if (item.kind === 'error') return;
    const timer = setTimeout(() => dismiss(item.id), 4500);
    return () => clearTimeout(timer);
  }, [item.id, item.kind, dismiss]);

  return (
    <div
      className={clsx(
        'pointer-events-auto flex animate-slide-up items-start gap-2 rounded-lg border p-3 shadow-lg backdrop-blur',
        className,
      )}
    >
      <Icon className="mt-px h-3.5 w-3.5 shrink-0" />
      <p className="flex-1 text-2xs leading-relaxed text-ink">{item.message}</p>
      <button
        className="shrink-0 opacity-60 transition-opacity hover:opacity-100"
        onClick={() => dismiss(item.id)}
        aria-label="Dismiss"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
